import { useMemo } from "react";
import usePlanInfoStore from "@/store/zustand/planInfoStore";
import { useSubscriptionInfo } from "./useSubscriptionInfo";

export const FREE_ALLOWED_PROMPTS_DAILY = 5;

export interface RemainingUsage {
  tokensLeft?: number;
  dailyPromptsLeft?: number;
  monthlyPromptsLeft?: number;
  extraPromptsInPackages?: number;
  subscriptionTier: string | null;
  usingPrompts: boolean;
  hasPromptsLeft: boolean;
  tokensLeftDisplay?: string;
}

export interface UsageLimitsResult {
  allowedPeriod: number;
  periodUsed: number;
  dayUsed: number;
  allowedDisplay: string | number;
  usedDisplay: string | number;
  remainingUsage: RemainingUsage;
}

export function useUsageLimits(): UsageLimitsResult {
  const { subscriptionTier, usingPrompts, extraPromptsInPackages } =
    useSubscriptionInfo();
  const usage = usePlanInfoStore((state) => state.usage);

  const allowedPeriod = useMemo(() => {
    if (!subscriptionTier) return 0;

    // New tiers (prompts)
    if (subscriptionTier === "free") return 30;
    if (subscriptionTier === "new_pro") return 150;
    if (subscriptionTier === "new_scale") return 250;
    if (subscriptionTier === "new_ultimate") return 555;
    if (subscriptionTier === "new_power") return 2150;

    // Old tiers (tokens)
    if (subscriptionTier === "pro") return 25_000_000;
    if (subscriptionTier === "scale") return 40_000_000;
    if (subscriptionTier === "ultimate") return 75_000_000;
    return 0;
  }, [subscriptionTier]);

  const periodUsed = useMemo(() => {
    if (!usage) return 0;

    if (usingPrompts) {
      return usage.total_prompts_period || 0;
    }

    return (
      (usage.total_input_tokens_period || 0) +
      (usage.total_output_tokens_period || 0)
    );
  }, [usage, usingPrompts]);

  const dayUsed = useMemo(() => {
    if (!usage) return 0;

    return usage.total_prompts_today || 0;
  }, [usage]);

  const allowedDisplay = useMemo(() => {
    if (
      subscriptionTier === "unlimited" ||
      subscriptionTier === "new_unlimited"
    )
      return "Unlimited";

    if (usingPrompts) {
      return allowedPeriod;
    }

    if (allowedPeriod < 1_000_000) return `${allowedPeriod / 1000}k`;
    return `${allowedPeriod / 1_000_000}M`;
  }, [allowedPeriod, subscriptionTier, usingPrompts]);

  const usedDisplay = useMemo(() => {
    if (usingPrompts) {
      return periodUsed;
    }

    if (periodUsed < 1_000_000) return `${Math.floor(periodUsed / 1000)}k`;
    return `${Math.round(periodUsed / 1_000_000)}M`;
  }, [periodUsed, usingPrompts]);

  const remainingUsage = useMemo((): RemainingUsage => {
    if (!usage)
      return {
        subscriptionTier,
        usingPrompts,
        // we want to prevent flicker of the usage indicator when loading since most users have prompts
        hasPromptsLeft: true,
      };

    const tokensLeft = usingPrompts ? undefined : allowedPeriod - periodUsed;

    if (subscriptionTier === "new_unlimited") {
      return {
        monthlyPromptsLeft: Infinity,
        subscriptionTier,
        usingPrompts: true,
        hasPromptsLeft: true,
      };
    }

    if (subscriptionTier === "unlimited") {
      return {
        tokensLeft: Infinity,
        subscriptionTier,
        usingPrompts: false,
        hasPromptsLeft: true,
      };
    }

    // users on new plan
    if (usingPrompts) {
      const monthlyPromptsLeft = allowedPeriod - periodUsed;

      if (subscriptionTier === "free") {
        let dailyPromptsLeft =
          FREE_ALLOWED_PROMPTS_DAILY - usage.total_prompts_today;
        const boundedMonthlyPromptsLeft = Math.max(monthlyPromptsLeft, 0);
        const boundedDailyPromptsLeft = Math.max(
          Math.min(dailyPromptsLeft, boundedMonthlyPromptsLeft),
          0
        );
        return {
          monthlyPromptsLeft: boundedMonthlyPromptsLeft,
          dailyPromptsLeft: boundedDailyPromptsLeft,
          subscriptionTier,
          usingPrompts,
          hasPromptsLeft:
            (boundedDailyPromptsLeft > 0 && boundedMonthlyPromptsLeft > 0) ||
            extraPromptsInPackages > 0,
          extraPromptsInPackages,
        };
      } else if (subscriptionTier.startsWith("new_")) {
        let boundedMonthlyPromptsLeft = Math.max(monthlyPromptsLeft, 0);
        return {
          monthlyPromptsLeft: boundedMonthlyPromptsLeft,
          extraPromptsInPackages:
            extraPromptsInPackages > 0 ? extraPromptsInPackages : undefined,
          subscriptionTier,
          usingPrompts,
          hasPromptsLeft: monthlyPromptsLeft > 0 || extraPromptsInPackages > 0,
        };
      }

      // if this is reached, then it's probably an invalid plan
      return {
        monthlyPromptsLeft: 0,
        dailyPromptsLeft: 0,
        subscriptionTier: "free",
        usingPrompts: true,
        hasPromptsLeft: false,
      };
    } else {
      // old users still use tokens
      const boundedTokensLeft = Math.max(tokensLeft || 0, 0);

      const tokensLeftDisplay =
        boundedTokensLeft >= 1_000_000
          ? `${Math.floor(boundedTokensLeft / 1_000_000)}M`
          : `${Math.floor(boundedTokensLeft / 1_000)}k`;

      return {
        tokensLeft: boundedTokensLeft,
        tokensLeftDisplay,
        subscriptionTier,
        usingPrompts,
        hasPromptsLeft: boundedTokensLeft > 0,
      };
    }
  }, [allowedPeriod, dayUsed, periodUsed, usage, usingPrompts]);

  return {
    allowedPeriod,
    periodUsed,
    dayUsed,
    allowedDisplay,
    usedDisplay,
    remainingUsage,
  };
}
